import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Briefcase,
  ShoppingCart,
  Store,
  UtensilsCrossed,
  Dumbbell,
  Warehouse,
  GraduationCap,
  Landmark,
  Pill,
  Sparkles,
  Activity,
  Droplets,
  Volume2,
  Heart,
  Zap,
  GitCompare,
  X,
  ShieldCheck,
  Wrench,
  Clock,
  Award,
  CheckCircle2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  industrySolutions,
  traitMeta,
  productFitDetails
} from '@/data/industrySolutions';
import { allProducts } from '@/data/products';

const iconMap = {
  Briefcase,
  ShoppingCart,
  Store,
  UtensilsCrossed,
  Dumbbell,
  Warehouse,
  GraduationCap,
  Landmark,
  Pill,
  Sparkles,
  Activity,
  Droplets,
  Volume2,
  Heart,
  Zap
};

const Icon = ({ name, className }) => {
  const Cmp = iconMap[name] || Sparkles;
  return <Cmp className={className} />;
};

/** Scale dot indicator (1–5) */
const TraitScale = ({ value }) => (
  <div className="flex items-center gap-1" data-testid={`trait-scale-${value}`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <span
        key={n}
        className={cn(
          'block w-1.5 h-4 rounded-sm',
          n <= value ? 'bg-primary' : 'bg-border'
        )}
      />
    ))}
  </div>
);

const ProductCard = ({ slug, expanded, onToggle, productLookup }) => {
  const product = productLookup[slug];
  const fit = productFitDetails[slug] || {};
  const img = product?.thumbnail || product?.image || product?.heroImage;
  const name = product?.title || product?.name || slug;

  return (
    <motion.div
      layout
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className={cn(
        'group relative rounded-2xl border border-border bg-card overflow-hidden',
        'hover:border-primary/50 hover:shadow-md transition-all duration-300'
      )}
      data-testid={`product-fit-card-${slug}`}
    >
      <button
        onClick={onToggle}
        className="w-full text-left"
        aria-expanded={expanded}
        data-testid={`product-fit-card-toggle-${slug}`}
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-muted">
          {img ? (
            <img
              src={img}
              alt={name}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-primary/40" />
            </div>
          )}
          {fit.warranty && (
            <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-[11px] font-semibold text-primary">
              <Award className="w-3 h-3" />
              {fit.warranty}
            </div>
          )}
        </div>
        <div className="p-4">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">
            Best for
          </p>
          <p className="text-sm font-medium text-foreground/90 mb-2 line-clamp-2 min-h-[2.5rem]">
            {fit.bestFor || 'Recommended product'}
          </p>
          <h4 className="font-heading font-semibold text-lg text-foreground mb-1.5">
            {name}
          </h4>
          {fit.primaryBenefit && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {fit.primaryBenefit}
            </p>
          )}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/60">
            <span className="text-xs font-semibold text-primary inline-flex items-center gap-1">
              {expanded ? 'Hide details' : 'Why it fits'}
              <ChevronDown
                className={cn(
                  'w-3.5 h-3.5 transition-transform duration-300',
                  expanded && 'rotate-180'
                )}
              />
            </span>
            <Link
              to={`/products/${slug}`}
              onClick={(e) => e.stopPropagation()}
              className="text-xs font-semibold text-foreground/70 hover:text-primary inline-flex items-center gap-1"
              data-testid={`product-fit-card-view-${slug}`}
            >
              View
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-border bg-muted/30"
          >
            <div className="p-4 space-y-3">
              {fit.whyFit && (
                <p className="text-sm text-foreground/85 leading-relaxed">{fit.whyFit}</p>
              )}
              <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                {fit.durability && (
                  <div className="flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-primary" />
                    <span className="text-muted-foreground">Durability:</span>
                    <span className="font-semibold text-foreground">{fit.durability}</span>
                  </div>
                )}
                {fit.maintenance && (
                  <div className="flex items-center gap-1.5">
                    <Wrench className="w-3.5 h-3.5 text-primary" />
                    <span className="text-muted-foreground">Maintenance:</span>
                    <span className="font-semibold text-foreground">{fit.maintenance}</span>
                  </div>
                )}
                {fit.installSpeed && (
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-primary" />
                    <span className="text-muted-foreground">Install:</span>
                    <span className="font-semibold text-foreground">{fit.installSpeed}</span>
                  </div>
                )}
                {fit.waterResistance && (
                  <div className="flex items-center gap-1.5">
                    <Droplets className="w-3.5 h-3.5 text-primary" />
                    <span className="text-muted-foreground">Water:</span>
                    <span className="font-semibold text-foreground">{fit.waterResistance}</span>
                  </div>
                )}
                {fit.acoustic && (
                  <div className="flex items-center gap-1.5">
                    <Volume2 className="w-3.5 h-3.5 text-primary" />
                    <span className="text-muted-foreground">Acoustic:</span>
                    <span className="font-semibold text-foreground">{fit.acoustic}</span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const CompareDrawer = ({ open, onClose, primaryId, candidates }) => {
  const [secondaryId, setSecondaryId] = useState(null);
  const primary = industrySolutions.find((i) => i.id === primaryId);
  const secondary = industrySolutions.find((i) => i.id === secondaryId);

  React.useEffect(() => {
    if (!open) setSecondaryId(null);
  }, [open]);

  if (!primary) return null;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 280 }}
            className="fixed inset-x-0 bottom-0 z-50 max-h-[88vh] overflow-y-auto rounded-t-3xl bg-card shadow-2xl"
            data-testid="compare-industries-drawer"
          >
            <div className="sticky top-0 z-10 bg-card/95 backdrop-blur border-b border-border px-5 py-4 flex items-center justify-between">
              <div>
                <h3 className="font-heading text-xl font-bold text-foreground">
                  Compare Industries
                </h3>
                <p className="text-xs text-muted-foreground">
                  See how operational priorities differ at a glance
                </p>
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-full hover:bg-muted flex items-center justify-center"
                aria-label="Close compare"
                data-testid="compare-drawer-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-5 grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Primary */}
              <div className="rounded-2xl border-2 border-primary/30 bg-primary/[0.04] p-5">
                <p className="text-[11px] uppercase tracking-wider font-semibold text-primary mb-2">
                  Industry A
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
                    <Icon name={primary.iconName} className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-lg text-foreground">
                      {primary.title}
                    </h4>
                    <p className="text-xs text-muted-foreground">{primary.descriptor}</p>
                  </div>
                </div>
              </div>

              {/* Secondary selector or details */}
              <div className="rounded-2xl border-2 border-dashed border-border p-5">
                <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                  Industry B
                </p>
                {!secondary ? (
                  <>
                    <p className="text-sm text-muted-foreground mb-3">
                      Pick another industry to compare against {primary.title}.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {candidates.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => setSecondaryId(c.id)}
                          className="px-3 py-1.5 text-xs font-semibold rounded-full border border-border hover:border-primary hover:bg-primary/5 transition-colors"
                          data-testid={`compare-pick-${c.id}`}
                        >
                          {c.title}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-secondary text-secondary-foreground flex items-center justify-center">
                      <Icon name={secondary.iconName} className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-heading font-bold text-lg text-foreground">
                        {secondary.title}
                      </h4>
                      <p className="text-xs text-muted-foreground">{secondary.descriptor}</p>
                    </div>
                    <button
                      onClick={() => setSecondaryId(null)}
                      className="text-xs font-semibold text-muted-foreground hover:text-primary"
                    >
                      Change
                    </button>
                  </div>
                )}
              </div>
            </div>

            {secondary && (
              <div className="px-5 pb-8">
                <div className="rounded-2xl border border-border overflow-hidden">
                  <div className="grid grid-cols-[1.4fr_1fr_1fr] bg-muted/60 px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
                    <span>Operational priority</span>
                    <span className="text-center">{primary.title}</span>
                    <span className="text-center">{secondary.title}</span>
                  </div>
                  {Object.entries(traitMeta).map(([key, meta], idx) => (
                    <div
                      key={key}
                      className={cn(
                        'grid grid-cols-[1.4fr_1fr_1fr] items-center px-4 py-3.5 text-sm',
                        idx % 2 === 0 ? 'bg-card' : 'bg-muted/20'
                      )}
                      data-testid={`compare-row-${key}`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon name={meta.icon} className="w-4 h-4 text-primary" />
                        <span className="font-medium text-foreground">{meta.label}</span>
                      </div>
                      <div className="flex justify-center">
                        <TraitScale value={primary.traits[key]} />
                      </div>
                      <div className="flex justify-center">
                        <TraitScale value={secondary.traits[key]} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

/** Mobile accordion section */
const MobileAccordion = ({ title, icon, children, defaultOpen = false, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border rounded-xl overflow-hidden bg-card">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3.5"
        aria-expanded={open}
        data-testid={testId}
      >
        <div className="flex items-center gap-2.5">
          {icon}
          <span className="font-heading font-semibold text-sm text-foreground">{title}</span>
        </div>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-muted-foreground transition-transform duration-300',
            open && 'rotate-180'
          )}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-border"
          >
            <div className="px-4 py-4">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const IndustrySolutionsExplorer = () => {
  const [activeId, setActiveId] = useState(industrySolutions[0].id);
  const [hoveredId, setHoveredId] = useState(null);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);

  const productLookup = useMemo(() => {
    const map = {};
    allProducts.forEach((p) => {
      if (p.slug) map[p.slug] = p;
    });
    return map;
  }, []);

  const previewId = hoveredId || activeId;
  const active = industrySolutions.find((i) => i.id === activeId);
  const preview = industrySolutions.find((i) => i.id === previewId) || active;
  const compareCandidates = industrySolutions.filter((i) => i.id !== activeId);

  const handleSelect = (id) => {
    setActiveId(id);
    setExpandedProduct(null);
  };

  return (
    <section
      className="section-padding bg-background"
      data-testid="industry-solutions-explorer"
      aria-labelledby="industry-solutions-heading"
    >
      <div className="container-portico">
        {/* Section Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10 md:mb-14 max-w-3xl mx-auto"
        >
          <span className="inline-block px-3 py-1 text-[11px] tracking-[0.18em] uppercase font-semibold text-primary bg-primary/10 rounded-full mb-4">
            Industry-Specific Solutions
          </span>
          <h2
            id="industry-solutions-heading"
            className="font-heading text-3xl md:text-4xl font-bold mb-4 text-foreground"
          >
            Find the flooring system built for your environment
          </h2>
          <p className="text-base md:text-lg text-muted-foreground">
            Pick your industry and we'll map the operational challenges, the systems
            engineered to solve them, and the products our specialists recommend.
          </p>
        </motion.div>

        {/* DESKTOP — Two column experience */}
        <div className="hidden lg:grid grid-cols-[300px_1fr] gap-6 xl:gap-10 items-start">
          {/* LEFT — Industry navigator */}
          <aside
            className="sticky top-28 self-start"
            aria-label="Industry navigator"
          >
            <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-3 px-1">
              Choose your industry
            </p>
            <ul className="space-y-1.5" role="tablist">
              {industrySolutions.map((ind) => {
                const isActive = ind.id === activeId;
                return (
                  <li key={ind.id}>
                    <button
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => handleSelect(ind.id)}
                      onMouseEnter={() => setHoveredId(ind.id)}
                      onMouseLeave={() => setHoveredId(null)}
                      onFocus={() => setHoveredId(ind.id)}
                      onBlur={() => setHoveredId(null)}
                      className={cn(
                        'group w-full text-left px-3.5 py-3 rounded-xl flex items-center gap-3',
                        'transition-all duration-200 border',
                        isActive
                          ? 'bg-primary text-primary-foreground border-primary shadow-md'
                          : 'bg-card border-transparent hover:border-primary/30 hover:bg-primary/[0.04] text-foreground'
                      )}
                      data-testid={`industry-tab-${ind.id}`}
                    >
                      <span
                        className={cn(
                          'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-200',
                          isActive
                            ? 'bg-white/15 text-primary-foreground'
                            : 'bg-primary/10 text-primary group-hover:bg-primary/15'
                        )}
                      >
                        <Icon name={ind.iconName} className="w-4.5 h-4.5" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-heading font-semibold text-sm leading-tight">
                          {ind.title}
                        </span>
                        <span
                          className={cn(
                            'block text-[11px] mt-0.5 truncate',
                            isActive
                              ? 'text-primary-foreground/80'
                              : 'text-muted-foreground'
                          )}
                        >
                          {ind.descriptor}
                        </span>
                      </span>
                      <ChevronRight
                        className={cn(
                          'w-4 h-4 flex-shrink-0 transition-all duration-200',
                          isActive
                            ? 'opacity-100 translate-x-0 text-primary-foreground'
                            : 'opacity-0 -translate-x-1 text-primary group-hover:opacity-60 group-hover:translate-x-0'
                        )}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>

            <button
              onClick={() => setCompareOpen(true)}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-border bg-card hover:border-primary hover:bg-primary/[0.04] text-foreground font-semibold text-sm transition-colors"
              data-testid="compare-industries-trigger-desktop"
            >
              <GitCompare className="w-4 h-4 text-primary" />
              Compare Industries
            </button>
          </aside>

          {/* RIGHT — Dynamic content panel */}
          <AnimatePresence mode="wait">
            <motion.div
              key={preview.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              className="min-w-0"
              data-testid={`industry-panel-${preview.id}`}
            >
              {/* Hero band */}
              <div className="relative rounded-3xl overflow-hidden mb-8 shadow-lg">
                <img
                  src={preview.image}
                  alt={preview.title}
                  className="w-full h-72 xl:h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-11 h-11 rounded-xl bg-white/95 text-primary flex items-center justify-center">
                      <Icon name={preview.iconName} className="w-5 h-5" />
                    </span>
                    <span className="px-2.5 py-1 rounded-full bg-white/15 backdrop-blur text-white text-[11px] uppercase tracking-wider font-semibold">
                      {preview.descriptor}
                    </span>
                  </div>
                  <h3 className="font-heading text-3xl xl:text-4xl font-bold text-white mb-2 max-w-xl">
                    {preview.title}
                  </h3>
                  <p className="text-white/90 text-sm md:text-base max-w-2xl leading-relaxed">
                    {preview.overview}
                  </p>
                </div>
              </div>

              {/* Problem / Solution / Benefit grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                <ContentColumn
                  badge="01"
                  title="Common Problems We Solve"
                  items={preview.problems}
                  tone="problem"
                  testId="problems-list"
                />
                <ContentColumn
                  badge="02"
                  title="How Portico Solves It"
                  items={preview.solutions}
                  tone="solution"
                  testId="solutions-list"
                />
                <ContentColumn
                  badge="03"
                  title="Operational Benefits"
                  items={preview.benefits}
                  tone="benefit"
                  testId="benefits-list"
                />
              </div>

              {/* Recommended product cards */}
              <div className="mb-10">
                <div className="flex items-end justify-between mb-5">
                  <div>
                    <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                      Specialist-recommended systems
                    </p>
                    <h4 className="font-heading text-2xl font-bold text-foreground">
                      Built for {preview.title.toLowerCase()}
                    </h4>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                  {preview.productSlugs.map((slug) => (
                    <ProductCard
                      key={slug}
                      slug={slug}
                      expanded={expandedProduct === slug}
                      onToggle={() =>
                        setExpandedProduct((cur) => (cur === slug ? null : slug))
                      }
                      productLookup={productLookup}
                    />
                  ))}
                </div>
              </div>

              {/* Smart recommendations bridge */}
              <SmartRecommendations industry={preview} />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* MOBILE — Sticky chips + accordions */}
        <div className="lg:hidden">
          <div
            className="sticky top-[64px] z-20 -mx-4 px-4 py-3 bg-background/95 backdrop-blur border-b border-border"
            data-testid="industry-mobile-rail"
          >
            <div className="flex gap-2 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-1">
              {industrySolutions.map((ind) => {
                const isActive = ind.id === activeId;
                return (
                  <button
                    key={ind.id}
                    onClick={() => handleSelect(ind.id)}
                    className={cn(
                      'snap-start flex-shrink-0 inline-flex items-center gap-2 px-3.5 py-2 rounded-full border text-xs font-semibold transition-all',
                      isActive
                        ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                        : 'bg-card border-border text-foreground'
                    )}
                    data-testid={`industry-chip-${ind.id}`}
                  >
                    <Icon name={ind.iconName} className="w-3.5 h-3.5" />
                    {ind.title}
                  </button>
                );
              })}
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3 }}
              className="mt-5 space-y-4"
              data-testid={`industry-panel-mobile-${active.id}`}
            >
              {/* Mobile hero */}
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src={active.image}
                  alt={active.title}
                  className="w-full h-48 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute inset-0 p-4 flex flex-col justify-end">
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-white/80 mb-1">
                    {active.descriptor}
                  </span>
                  <h3 className="font-heading text-2xl font-bold text-white">
                    {active.title}
                  </h3>
                </div>
              </div>

              <p className="text-sm text-muted-foreground leading-relaxed px-1">
                {active.overview}
              </p>

              <MobileAccordion
                title="Common Problems"
                icon={<Activity className="w-4 h-4 text-amber-600" />}
                defaultOpen
                testId="mobile-problems-toggle"
              >
                <ul className="space-y-2.5">
                  {active.problems.map((p) => (
                    <li key={p} className="flex gap-2.5 text-sm text-foreground/85">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              </MobileAccordion>

              <MobileAccordion
                title="How Portico Solves It"
                icon={<ShieldCheck className="w-4 h-4 text-primary" />}
                testId="mobile-solutions-toggle"
              >
                <ul className="space-y-2.5">
                  {active.solutions.map((s) => (
                    <li key={s} className="flex gap-2.5 text-sm text-foreground/85">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      {s}
                    </li>
                  ))}
                </ul>
              </MobileAccordion>

              <MobileAccordion
                title="Operational Benefits"
                icon={<Award className="w-4 h-4 text-emerald-600" />}
                testId="mobile-benefits-toggle"
              >
                <ul className="space-y-2.5">
                  {active.benefits.map((b) => (
                    <li key={b} className="flex gap-2.5 text-sm text-foreground/85">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                      {b}
                    </li>
                  ))}
                </ul>
              </MobileAccordion>

              <MobileAccordion
                title="Recommended Systems"
                icon={<Sparkles className="w-4 h-4 text-primary" />}
                testId="mobile-products-toggle"
              >
                <div
                  className="flex gap-3 overflow-x-auto snap-x snap-mandatory -mx-4 px-4 pb-2 no-scrollbar"
                  data-testid="mobile-product-swiper"
                >
                  {active.productSlugs.map((slug) => (
                    <div
                      key={slug}
                      className="snap-start flex-shrink-0 w-[78%] max-w-xs"
                    >
                      <ProductCard
                        slug={slug}
                        expanded={expandedProduct === slug}
                        onToggle={() =>
                          setExpandedProduct((cur) => (cur === slug ? null : slug))
                        }
                        productLookup={productLookup}
                      />
                    </div>
                  ))}
                </div>
              </MobileAccordion>

              <SmartRecommendations industry={active} mobile />

              <button
                onClick={() => setCompareOpen(true)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-border bg-card text-foreground font-semibold text-sm"
                data-testid="compare-industries-trigger-mobile"
              >
                <GitCompare className="w-4 h-4 text-primary" />
                Compare with another industry
              </button>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <CompareDrawer
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        primaryId={activeId}
        candidates={compareCandidates}
      />

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </section>
  );
};

const toneStyles = {
  problem: {
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    bullet: 'bg-amber-500'
  },
  solution: {
    badge: 'bg-primary/10 text-primary border-primary/20',
    bullet: 'bg-primary'
  },
  benefit: {
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    bullet: 'bg-emerald-600'
  }
};

const ContentColumn = ({ badge, title, items, tone, testId }) => {
  const styles = toneStyles[tone];
  return (
    <div
      className="rounded-2xl border border-border bg-card p-5 md:p-6 hover:shadow-md transition-shadow duration-300"
      data-testid={testId}
    >
      <div className="flex items-center gap-2.5 mb-4">
        <span
          className={cn(
            'inline-flex items-center justify-center w-9 h-9 rounded-lg border text-sm font-heading font-bold',
            styles.badge
          )}
        >
          {badge}
        </span>
        <h4 className="font-heading font-bold text-base text-foreground leading-tight">
          {title}
        </h4>
      </div>
      <ul className="space-y-2.5">
        {items.map((item) => (
          <li key={item} className="flex gap-2.5 text-sm text-foreground/85 leading-relaxed">
            <span className={cn('mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0', styles.bullet)} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const SmartRecommendations = ({ industry, mobile }) => (
  <div
    className={cn(
      'rounded-2xl bg-gradient-to-br from-primary to-[#0a4d8c] text-primary-foreground p-6 md:p-8 relative overflow-hidden',
      mobile && 'p-5'
    )}
    data-testid="smart-recommendations"
  >
    <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-white/5 blur-2xl" aria-hidden />
    <div className="relative">
      <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-primary-foreground/70 mb-2">
        Smart recommendations
      </p>
      <h4 className="font-heading text-xl md:text-2xl font-bold mb-4 max-w-2xl">
        Based on a {industry.title.toLowerCase()} environment, we recommend:
      </h4>
      <ul className="space-y-2.5 mb-6">
        {industry.smartRecs.map((r, i) => (
          <li key={r} className="flex items-start gap-3 text-sm md:text-base text-primary-foreground/95">
            <span className="mt-1 w-6 h-6 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0 text-xs font-bold">
              {i + 1}
            </span>
            <span>{r}</span>
          </li>
        ))}
      </ul>
      <div className="flex flex-col sm:flex-row gap-3">
        <Link to="/flooring-selector" className="w-full sm:w-auto">
          <Button
            variant="secondary"
            className="w-full sm:w-auto font-semibold"
            data-testid="smart-rec-find-solution"
          >
            Find the Right Solution
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </Link>
        <Link to="/contact" className="w-full sm:w-auto">
          <Button
            variant="outline"
            className="w-full sm:w-auto font-semibold border-primary-foreground/40 text-primary-foreground bg-transparent hover:bg-white/10 hover:text-primary-foreground"
            data-testid="smart-rec-talk-specialist"
          >
            Talk to an Industry Specialist
          </Button>
        </Link>
      </div>
    </div>
  </div>
);

export default IndustrySolutionsExplorer;
